import React, { useState, useEffect } from 'react';
import MetaTags from 'react-meta-tags';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Button, Row, Col, Card, CardBody } from 'reactstrap';
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';
import { MoonLoader } from 'react-spinners';
import styled from 'styled-components';

import { useAuth } from 'hooks/useAuth';
import { getFirebaseBackend } from '../../helpers/firebaseHelper';
import '../../assets/scss/datatables.scss';
import { actionButton } from './components/Action-Button';
import { getMeetingColumns } from './config/meetingTableColumns';
import { getDefaultSorted, getPageOptions } from './config/tableConfig';
import { handleDownloadMeetingCSV } from 'utils';

const Meetings = () => {
  const firebaseHelper = getFirebaseBackend();
  const { t: translate } = useTranslation();
  const { SearchBar } = Search;
  const { user: userObj } = useAuth();
  const accountId = userObj.account;
  const [userData, setUserData] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchMeetings = async () => {
    if (!accountId) return;
    const meetings = await firebaseHelper.getAllMeetings(accountId);

    const newUserData = meetings
      ? Object.values(meetings).map((meeting, index) => {
          return {
            id: meeting.id,
            dateValue: meeting.date ?? 0,
            date: meeting.date ? new Date(meeting.date).toLocaleDateString() : 'N/A',
            agenda: meeting.agenda,
            minutesLong: meeting.minutes,
            tasks: meeting.relatedTasks ? meeting.relatedTasks.length : '0',
            action: actionButton(meeting, userObj.adminStatus, accountId, translate),
          };
        })
      : [];
    setUserData(newUserData);
  };

  const canCreateMeeting = (user) => {
    return ['Super Admin', 'Admin'].includes(user.adminStatus);
  };

  const canDownloadCSV = (user) => {
    return ['Super Admin', 'Admin', 'Client Admin'].includes(user.adminStatus);
  };

  const handleDownloadCSV = async () => {
    try {
      setIsDownloading(true);
      await handleDownloadMeetingCSV(accountId);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, [accountId]);

  return (
    <React.Fragment>
      <div className="page-content" style={{ minHeight: '100vh' }}>
        <MetaTags>
          <title>{translate('List Meetings')} | Ectools</title>
        </MetaTags>
        <Container fluid>
          <Row>
            <Col xs="12" className="mb-4">
              <h4 className="mb-0 font-size-18">{translate('Meetings')}</h4>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <PaginationProvider
                    pagination={paginationFactory(getPageOptions(userData.length))}
                    keyField="id"
                    columns={getMeetingColumns(translate)}
                    data={userData}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <ToolkitProvider
                        keyField="id"
                        columns={getMeetingColumns(translate)}
                        data={userData}
                        search
                      >
                        {(toolkitProps) => (
                          <React.Fragment>
                            <Row className="mb-3 d-flex justify-content-between">
                              <Col md="4" className="d-flex align-items-center ">
                                <div className="search-box d-inline-block position-relative d-flex align-items-center">
                                  <SearchBar
                                    {...toolkitProps.searchProps}
                                    placeholder={translate('Search')}
                                  />
                                  <i className="bx bx-search-alt search-icon" />
                                </div>
                              </Col>
                              <Col
                                md="4"
                                className="d-flex flex-row-reverse align-items-center gap-2"
                              >
                                {canCreateMeeting(userObj) && (
                                  <Button
                                    tag={Link}
                                    to="/create-meeting"
                                    color="primary"
                                    className="d-flex gap-2 align-items-center"
                                  >
                                    <i className="bx bx-plus-circle" />
                                    {translate('Create New Meeting')}
                                  </Button>
                                )}
                                {canDownloadCSV(userObj) && (
                                  <ButtonDownloadMeetingStyled
                                    className="d-flex gap-2 align-items-center"
                                    onClick={handleDownloadCSV}
                                  >
                                    {isDownloading ? (
                                      <MoonLoader
                                        size={15}
                                        color="#2d3748"
                                        loading={isDownloading}
                                      />
                                    ) : (
                                      <>
                                        <i className="bx bx-download" />
                                        {translate('Export CSV')}
                                      </>
                                    )}
                                  </ButtonDownloadMeetingStyled>
                                )}
                              </Col>
                            </Row>

                            <Row>
                              <Col xl="12">
                                <div className="table-responsive">
                                  <BootstrapTable
                                    keyField="id"
                                    responsive
                                    hover
                                    bordered={false}
                                    striped={false}
                                    defaultSorted={getDefaultSorted}
                                    classes={'table align-middle table-nowrap'}
                                    headerWrapperClasses={'thead-light'}
                                    {...toolkitProps.baseProps}
                                    {...paginationTableProps}
                                  />
                                </div>
                              </Col>
                            </Row>

                            <Row className="align-items-md-center mt-30">
                              <Col className="inner-custom-pagination d-flex">
                                <div className="d-inline">
                                  <SizePerPageDropdownStandalone {...paginationProps} />
                                </div>
                                <div className="text-md-right ms-auto">
                                  <PaginationListStandalone {...paginationProps} />
                                </div>
                              </Col>
                            </Row>
                          </React.Fragment>
                        )}
                      </ToolkitProvider>
                    )}
                  </PaginationProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

const ButtonDownloadMeetingStyled = styled(Button)`
  min-width: 8rem;
  border: 1px solid #e2e8f0;
  background-color: transparent;
  color: #343a40 !important;
  font-weight: 500;
  &:hover {
    background-color: #e2e8f0;
    border: 1px solid #e2e8f0;
  }
`;

export default Meetings;
